// F37.5-ANDROID-SHARE-INTENT — TS bindings for the custom Android ShareIntent
// Capacitor plugin (see android/app/src/main/java/com/like2fit/app/
// ShareIntentPlugin.java) and the iOS Share Extension (ios/App/Like2FitShare +
// ios/App/App/ShareIntent.swift). Web remains a no-op (returns type="none").

import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export type DetectedSourcePlatform =
  | "instagram"
  | "tiktok"
  | "pinterest"
  | "facebook"
  | null;

export type SharePayload =
  | { type: "none" }
  | {
      type: "text";
      text: string;
      sourcePlatform?: DetectedSourcePlatform;
      // Phase 3b — optional preview image (og:image JPEG) the iOS share
      // extension fetched and the native plugin moved into Capacitor Cache.
      // Read via @capacitor/filesystem (directory: 'cache'). Absent on
      // Android and on iOS when og:image fetch failed.
      previewImage?: string;
      previewDirectory?: "cache";
      previewMimeType?: string;
    }
  | {
      type: "image";
      // F.5d — file-path transport. Native plugin writes bytes to the
      // Capacitor Cache directory (e.g. "shares/share_<UUID>.jpg") and
      // returns the relative path; JS reads it via @capacitor/filesystem
      // and uploads binary to storage. Base64 transport is removed.
      filename: string;
      directory: "cache";
      mimeType: string;
      sourcePlatform?: DetectedSourcePlatform;
    };

interface ShareIntentPluginShape {
  consumePending(): Promise<SharePayload>;
  addListener(
    eventName: "shareReceived",
    listener: (payload: SharePayload) => void,
  ): Promise<PluginListenerHandle>;
}

const ShareIntent = registerPlugin<ShareIntentPluginShape>("ShareIntent", {
  // Web/iOS fallback — returns no-pending so callers can call this
  // unconditionally without platform checks.
  web: {
    async consumePending(): Promise<SharePayload> {
      return { type: "none" };
    },
    async addListener() {
      return { remove: async () => {} } as PluginListenerHandle;
    },
  },
});

export function isShareIntentAvailable(): boolean {
  // Android and iOS both have native ShareIntent implementations.
  const available =
    Capacitor.isNativePlatform() && ["android", "ios"].includes(Capacitor.getPlatform());
  // F37.5-SHARE-BRIDGE-BUG diagnostic — temporary
  console.log("[share-debug] isShareIntentAvailable=", available, "platform=", Capacitor.getPlatform(), "native=", Capacitor.isNativePlatform());
  return available;
}

export async function consumePendingShare(): Promise<SharePayload> {
  console.log("[share-debug] consumePendingShare called");
  if (!isShareIntentAvailable()) return { type: "none" };
  try {
    const result = await ShareIntent.consumePending();
    console.log("[share-debug] consumePending result:", JSON.stringify({ type: result.type, hasB64: "base64" in result }));
    return result;
  } catch (err) {
    console.error("[share-intent] consumePending failed", err);
    return { type: "none" };
  }
}

export async function addShareReceivedListener(
  handler: (payload: SharePayload) => void,
): Promise<PluginListenerHandle> {
  console.log("[share-debug] addShareReceivedListener registering");
  if (!isShareIntentAvailable()) {
    return { remove: async () => {} } as PluginListenerHandle;
  }
  return ShareIntent.addListener("shareReceived", (payload) => {
    console.log("[share-debug] LISTENER FIRED, type=", payload.type);
    handler(payload);
  });
}

export default ShareIntent;
